import { normalizeKeyForLookup } from '../stagedFiles';
import { getFileFieldId } from '../fileHelpers';

/**
 * Collect staged files relevant to a repeater field's nested fields.
 *
 * This utility is used by both TabsField and IDSSubformRenderer to
 * determine initial staged files for repeater fields loaded from drafts.
 *
 * @param {Object} repeaterField - The repeater field definition ({ fields: [...] })
 * @param {Object} draftStagedFiles - Staged files map keyed by lookup key
 * @param {number|string} formId - Current form ID
 * @param {number|string} subformId - Current subform ID
 * @returns {Array} Array of file objects with fieldId/field_id set
 */
export function getRepeaterInitialStagedFiles(
  repeaterField,
  draftStagedFiles,
  formId,
  subformId
) {
  if (!draftStagedFiles || typeof draftStagedFiles !== 'object') {
    return [];
  }

  const nestedFields = Array.isArray(repeaterField?.fields)
    ? repeaterField.fields
    : [];
  const nestedFieldIds = nestedFields
    .map((fieldItem) => fieldItem?.id)
    .filter(Boolean);
  if (nestedFieldIds.length === 0) return [];

  const subformPrefix = `${String(subformId || '')}_`;
  const matchesNestedField = (fieldId) =>
    nestedFieldIds.some(
      (nestedId) =>
        fieldId === nestedId ||
        fieldId.startsWith(`${nestedId}-`) ||
        fieldId.startsWith(`${nestedId}__item_`)
    );

  const collected = [];
  Object.entries(draftStagedFiles).forEach(([key, files]) => {
    const normalizedKey = normalizeKeyForLookup(key, formId);
    if (!normalizedKey || !normalizedKey.startsWith(subformPrefix)) return;

    const extractedFieldId = normalizedKey.slice(subformPrefix.length);
    if (!extractedFieldId || !matchesNestedField(extractedFieldId)) return;

    (files || []).forEach((file) => {
      const resolvedFieldId = getFileFieldId(file) || extractedFieldId;
      collected.push({
        ...file,
        fieldId: resolvedFieldId,
        // eslint-disable-next-line camelcase
        field_id: resolvedFieldId,
      });
    });
  });

  return collected;
}
